import React, {
    useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { WizardPage, wizardProps } from 'gw-portals-wizard-react';
import { RenewalService } from 'gw-capability-renewal';
import { SgPaymentUtils, SgSessionStorageService, useSgTrackPageName } from 'saga-modules';
import { CepConstants } from 'saga-cep-specific-components';
import SgCepSideMenu from 'sg-cep-side-menu';
import CommonConstants from 'sg-common-constants';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { BreakpointTrackerContext } from '@jutro/layout';
import { AccountBillingDetailsService } from 'gw-capability-billing';
import SgCreditCardDetails from 'sg-credit-card-details';
import SgNewCardDetails from 'sg-new-card-details';
import SgFailedDownloadPayment from 'sg-failed-download-payment';
import SgCepLobSpecificUtil from 'sg-cep-lob-specific-util';
import { Loader } from '@jutro/components';
import { SgDirectDebitDetails, SgDirectDebitForm } from 'sg-direct-debit-details';
import { useValidation } from 'gw-portals-validation-react';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import messages from './SgRenewalPaymentPage.messages';
import metadata from './SgRenewalPaymentPage.metadata.json5';

function SgRenewalPaymentPage(props) {
    const {
        id,
        wizardData: modelVM,
        updateWizardData: updateData,
        goNext,
        sgWizardPropsExt
    } = props;
    const {
        handleCancel
    } = sgWizardPropsExt;
    const {
        onValidate,
        isComponentValid,
    } = useValidation(id);

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    const existingPaymentInstrument = useRef({});
    const [showErrors, setShowErrors] = useState(false);
    const [isLoading, setLoadingState] = useState(true);
    const [paymentDetails, setPaymentDetails] = useState(undefined);
    const [paymentInstrumentVM, setPaymentInstrumentVM] = useState(SgPaymentUtils.createPaymentInstrumentVM(viewModelService));
    const [pageName, setPageName] = useState(undefined);
    const [showFailedDownloadMsg, setShowFailedDownloadMsg] = useState(false);
    const [showChangeDetailsForm, setShowChangeDetailsForm] = useState(false);

    useSgTrackPageName(pageName);

    const {
        totalCost,
        paymentMethodCode,
        paymentPlanCode,
    } = modelVM.value;

    const commonProps = useMemo(() => {
        return {
            policyNumber: modelVM.value.policyNumber,
            paymentInstrumentVM,
            setPaymentInstrumentVM,
            setLoadingState,
            setPaymentDetails,
            setPageName,
            pageName: CepConstants.PAGE_NAMES.RENEWAL_CURRENT_PAYMENT_DETAILS,
            existingPaymentInstrument,
            setShowChangeDetailsForm,
            isMigrated: modelVM.value.isMigrated
        };
    }, []);
    const isImmediatePaymentRequired = SgPaymentUtils.isImmediatePaymentRequired(paymentPlanCode, paymentMethodCode, totalCost.amount);
    const isDirectDebit = SgPaymentUtils.isDirectDebit(paymentMethodCode);
    const isCreditCard = SgPaymentUtils.isCreditCardPaymentMethod(paymentMethodCode);
    const contactUsPhoneNumber = CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_PAYMENT_PHONE_NUMBER;

    useEffect(() => {
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_CREDITCARD_CHANGED, false);
        SgPaymentUtils.initPaymentDetails(commonProps);
    }, []);

    const paymentDetailsHeaderContent = useMemo(() => {
        if (!isDirectDebit) {
            return translator(messages.ContinueCardPayment);
        } if (showChangeDetailsForm) {
            return translator(messages.UpdateDetails);
        }
        return translator(messages.CurrentDetails);
    }, [isDirectDebit, showChangeDetailsForm]);

    const changePaymentDetails = useCallback(() => {
        setPageName(isImmediatePaymentRequired
            ? CepConstants.PAGE_NAMES.RENEWAL_NEW_PAYMENT_DETAILS : CepConstants.PAGE_NAMES.RENEWAL_UPDATE_PAYMENT_DETAILS);
        if (!isDirectDebit) {
            SgSessionStorageService.setItem(SgSessionStorageService.KEY.IS_CREDITCARD_CHANGED, true);
        }
        setShowChangeDetailsForm(true);
    }, [isDirectDebit, isImmediatePaymentRequired]);

    const bind = useCallback(() => {
        setPageName(CepConstants.PAGE_NAMES.RENEWAL_PAYMENT_PROCESS_END);
        RenewalService.bind(
            modelVM.value.jobID, modelVM.value.branchNumber,
            { paymentMethod: SgPaymentUtils.getPaymentMethod(paymentMethodCode, totalCost) }, modelVM.value.migratedPaymentDetails
        ).then(() => {
            goNext();
        }).catch((err) => {
            const error = { error: err, customErrorMessage: 'Exception during binding' };
            throw error;
        }).finally(() => {
            setLoadingState(false);
        });
    }, [goNext, modelVM, paymentMethodCode, totalCost]);

    const payWithDirectDebit = useCallback(() => {
        AccountBillingDetailsService.setNewPaymentInstrument(paymentDetails.internalId, paymentInstrumentVM.value).then(() => {
            const newPaymentDetails = _.clone(paymentDetails);
            _.set(newPaymentDetails, 'paymentInstrument', paymentInstrumentVM);
            setPaymentDetails(newPaymentDetails);
            bind();
        }).catch((err) => {
            _.set(err, 'customErrorMessage', 'Could not set new payment instrument');
            throw err;
        }).finally(() => {
            setLoadingState(false);
        });
    }, [bind, paymentDetails, paymentInstrumentVM]);

    const validateBankAccountAndPay = useCallback(() => {
        if (!_.isEqual(paymentInstrumentVM.value, existingPaymentInstrument.current)) {
            paymentInstrumentVM.creditCardData = null;
            paymentInstrumentVM.value.quoteID = modelVM.value.policyNumber;
            SgPaymentUtils.bankAccountValidation(paymentInstrumentVM, {}, payWithDirectDebit);
        } else {
            bind();
        }
    }, [bind, modelVM, payWithDirectDebit, paymentInstrumentVM]);

    const onContinue = useCallback(() => {
        if (!isComponentValid) {
            setShowErrors(true);
            return;
        }
        setLoadingState(true);
        if (isImmediatePaymentRequired) {
            SgPaymentUtils.payWithExistingCard(modelVM.value.policyNumber, modelVM.value.jobID,
                paymentInstrumentVM.value.publicID, modelVM.value.branchNumber, bind)
                .catch((error) => {
                    setLoadingState(false);
                    throw error;
                });
        } else {
            validateBankAccountAndPay();
        }
    }, [bind, isComponentValid, isImmediatePaymentRequired, modelVM, paymentInstrumentVM, validateBankAccountAndPay]);

    const cancelUpdatingPaymentDetails = useCallback(() => {
        SgPaymentUtils.cancelUpdatingPaymentDetails(commonProps);
        setPageName(CepConstants.PAGE_NAMES.RENEWAL_CURRENT_PAYMENT_DETAILS);
    }, [commonProps]);

    const differentPaymentOptionContent = useMemo(() => {
        return (
            <p>
                {translator(messages.DifferentPaymentOption, {
                    phoneNumber: isMobile
                        ? <a href={`tel:${contactUsPhoneNumber.replaceAll(' ', '')}`}>{contactUsPhoneNumber}</a>
                        : contactUsPhoneNumber
                })}
            </p>
        );
    }, [translator, isMobile, contactUsPhoneNumber]);

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const overrideProps = {
        differentPaymentOption: {
            content: differentPaymentOptionContent,
            visible: !SgCepLobSpecificUtil.shouldHideDifferentPaymentOptionMessage(modelVM)
        },
        transactionCost: {
            value: totalCost.amount
        },
        requireImmediatePayment: {
            visible: isImmediatePaymentRequired
        },
        paymentDetailsHeader: {
            content: paymentDetailsHeaderContent
        },
        currentDetailsContainer: {
            visible: !showChangeDetailsForm || !isCreditCard
        },
        creditCardDetails: {
            visible: false, // fix to remove adobe target de9089, left the code to be used later
            paymentDetails,
            paymentInstrumentVM
        },
        directDebitDetails: {
            visible: isDirectDebit && !showChangeDetailsForm,
            paymentDetails,
            paymentInstrumentVM
        },
        directDebitForm: {
            visible: isDirectDebit && showChangeDetailsForm,
            paymentDetails,
            paymentInstrumentVM,
            updateData: setPaymentInstrumentVM,
            onValidate,
            showErrors
        },
        cancelLink: {
            visible: !showChangeDetailsForm
        },
        cancelUpdatingButton: {
            visible: showChangeDetailsForm
        },
        changePaymentButton: {
            id: isCreditCard ? 'changeCardButton' : 'changePaymentButton',
            visible: !showChangeDetailsForm && isDirectDebit,
            content: isImmediatePaymentRequired ? translator(messages.NewCard) : translator(messages.ChangePayment)
        },
        continuePaymentButton: {
            content: isImmediatePaymentRequired ? translator(messages.Purchase) : translator(messages.Confirm),
            visible: !showChangeDetailsForm || !isCreditCard,
            onClick: !isDirectDebit ? changePaymentDetails : onContinue
        },
        newCardDetails: {
            modelVM,
            updateData,
            bind,
            productCode: modelVM.baseData.productCode.value,
            setShowFailedDownloadMsg: setShowFailedDownloadMsg,
            visible: showChangeDetailsForm && isCreditCard,
            isRenewal: true
        },
        failedDownloadMessage: {
            showFailedDownloadMsg: showFailedDownloadMsg
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            changePaymentDetails,
            handleCancel,
            onContinue,
            cancelUpdatingPaymentDetails
        },
        resolveComponentMap: {
            SgCreditCardDetails,
            SgDirectDebitDetails,
            SgDirectDebitForm,
            SgNewCardDetails,
            SgFailedDownloadPayment,
            SgCepSideMenu
        }
    };

    return (
        <WizardPage showPrevious={false} showNext={false}>
            {renderContentFromMetadata(metadata.pageContent, overrideProps, resolvers)}
        </WizardPage>
    );
}

SgRenewalPaymentPage.propTypes = wizardProps;
export default SgRenewalPaymentPage;
